import { ArrowLeft } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { LEGAL_INFO } from './legal-info';

interface Props {
  title: string;
  subtitle?: string;
  children: ReactNode;
}

/**
 * Layout partagé des pages légales (mentions, CGV, CGU, confidentialité, cookies).
 * Accessibles publiquement (pas d'authentification) ; rendu sobre, lisible.
 */
export function LegalLayout({ title, subtitle, children }: Props) {
  return (
    <div className="min-h-screen bg-cream">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-6 py-4">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Retour
          </Link>
          <span className="text-sm font-semibold text-slate-700">{LEGAL_INFO.productName}</span>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-10">
        <header className="mb-8 border-b border-slate-200 pb-6">
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">{title}</h1>
          {subtitle && <p className="mt-2 text-sm text-slate-600">{subtitle}</p>}
          <p className="mt-3 text-xs text-slate-500">
            Dernière mise à jour : {LEGAL_INFO.lastUpdated} — version {LEGAL_INFO.version}
          </p>
        </header>

        <article className="prose prose-slate max-w-none space-y-6 text-sm leading-relaxed text-slate-700 [&_h2]:mt-8 [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:text-slate-900 [&_h3]:mt-4 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-slate-900 [&_ul]:list-disc [&_ul]:pl-6 [&_ul_li]:my-1 [&_strong]:text-slate-900 [&_a]:text-brand [&_a:hover]:underline">
          {children}
        </article>

        <footer className="mt-12 flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-6 text-xs text-slate-500">
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            <Link to="/legal/mentions-legales" className="hover:text-slate-900">
              Mentions légales
            </Link>
            <Link to="/legal/cgv" className="hover:text-slate-900">
              CGV
            </Link>
            <Link to="/legal/cgu" className="hover:text-slate-900">
              CGU
            </Link>
            <Link to="/legal/confidentialite" className="hover:text-slate-900">
              Confidentialité
            </Link>
            <Link to="/legal/cookies" className="hover:text-slate-900">
              Cookies
            </Link>
          </div>
          <span>© {LEGAL_INFO.companyName}</span>
        </footer>
      </main>
    </div>
  );
}
